import { useContext, useEffect, useState } from 'react'
import Forms from 'contexts/Forms'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import message from 'antd/lib/message'
import config from 'config'
import generatePDF from 'utils/generatePdf'
import { IQuickSearch } from 'interfaces/quickSearch.interface'
import { IQuoteResponse } from 'interfaces/quoteResult.interface'
import { initialQuickSearchTabs } from '../staticData'

const useQuoteResult = () => {
  const navigate = useNavigate()
  const {
    quoteResultLoading,
    quoteResultActiveTab,
    setQuoteResultActiveTab,
    quickSearch: { setActiveTab },
  } = useContext(Forms)
  const quickSearchData: IQuickSearch = JSON.parse(localStorage.getItem('quickSearch') || '{}')
  const [loading, setLoading] = useState(quoteResultLoading)
  const [pdfReportLoading, setPdfReportLoading] = useState(false)
  const [error, setError] = useState<boolean | string>(quickSearchData.error)
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  const [serviceProviderReference, setServiceProviderReference] = useState('')
  const [quoteResponseIdentifier, setQuoteResponseIdentifier] = useState('')

  useEffect(() => {
    setLoading(quoteResultLoading)

    if (!idNumber && !quoteResultLoading) {
      const newIdNumber = Number(localStorage.getItem('idNumber'))
      navigate(`/quote-result?idNumber=${newIdNumber}`)
    }

    if (!quoteResultLoading) {
      const quoteResult: IQuoteResponse = JSON.parse(localStorage.getItem('quoteResult') || '{}')
      if (quoteResult?.successFullQuotes?.length > 0) {
        setServiceProviderReference(quoteResult.successFullQuotes[0]?.$?.['perqresp:service_provider_reference'] || '')
        setQuoteResponseIdentifier(quoteResult.successFullQuotes[0]?.$?.['perqresp:quote_response_identifier'] || '')
      }

      const updatedQuickSearchData: IQuickSearch = JSON.parse(localStorage.getItem('quickSearch') || '{}')
      if (updatedQuickSearchData.error) {
        setError(updatedQuickSearchData.error)
      } else if (!quoteResult?.successFullQuotes?.length) {
        setError('No matching quotes found for your criteria. Please adjust your input and try again.')
      } else {
        setError(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteResultLoading])

  const clientDetails: any = quickSearchData?.clientDetails?.firstLife || {}
  const furtherInformation: any = quickSearchData?.furtherInformation || {}
  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-GB')

  const handleTabChange = (key: string) => {
    setQuoteResultActiveTab(key)
  }

  const handleGoBackBtnClick = () => {
    navigate(`/quick-search?idNumber=${idNumber}`)
  }

  const handlePdfReport = async () => {
    try {
      setPdfReportLoading(true)

      const response = await fetch(`${config.API_URL}/iress/comparison-print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          documentReferenceId: Number(serviceProviderReference),
          quoteResponseIdentifier: quoteResponseIdentifier,
        }),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        throw new Error(errorBody?.message || 'Failed to generate PDF report')
      }

      const responseData = await response.json()

      const base64String = responseData[0]
      const filename: string = 'report.pdf'

      generatePDF(base64String, filename)
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('action', 'comparisonPrint')
        scope.setExtra('serviceProviderReference', serviceProviderReference)
        scope.setExtra('quoteResponseIdentifier', quoteResponseIdentifier)
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)))
      })
      message.error('Failed to generate PDF report. Please try again.')
    } finally {
      setPdfReportLoading(false)
    }
  }

  const handleRequote = () => {
    setActiveTab(initialQuickSearchTabs.clientDetails)
    navigate(`/quick-search?idNumber=${idNumber}`)
  }

  return {
    loading,
    clientDetails,
    furtherInformation,
    formattedDate,
    quoteResultActiveTab,
    handleTabChange,
    handleGoBackBtnClick,
    error,
    handlePdfReport,
    pdfReportLoading,
    handleRequote,
    serviceProviderReference,
  }
}

export default useQuoteResult
